import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PopupContext } from 'components';
import { useContext } from 'react';
import { getPageParamFromLastPage } from 'utils/getPageParamFromLastPage';
import PRODUCTS_KEYS from '../products.keys';
import PRODUCTS_MUTATIONS from '../products.mutation';
import PRODUCTS_QUERIES from '../products.queries';

export const useFetchProductCategories = ({ page, pageSize, search, ordering, filters }) =>
  useQuery({
    queryKey: PRODUCTS_KEYS.PRODUCT_CATEGORIES_PAGINATED({
      page,
      pageSize,
      search,
      ordering,
      filters,
    }),
    queryFn: () =>
      PRODUCTS_QUERIES.FETCH_PRODUCT_CATEGORIES({
        page,
        pageSize,
        search,
        ordering,
        filters,
      }),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useFetchProductCategoriesInfinite = ({ pageSize, search, ordering, filters }) =>
  useInfiniteQuery({
    queryKey: PRODUCTS_KEYS.PRODUCT_CATEGORIES_INFINITE({ pageSize, filters, search, ordering }),
    queryFn: ({ pageParam = 1 }) =>
      PRODUCTS_QUERIES.FETCH_PRODUCT_CATEGORIES({
        page: pageParam,
        pageSize,
        search,
        ordering,
        filters,
      }),
    getNextPageParam: (lastPage) => getPageParamFromLastPage(lastPage),
    staleTime: 1 * 60 * 1000, // 1 minute
  });

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);
  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.CREATE_CATEGORY,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_CATEGORIES() });
      closePopup?.();
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  const { closePopup } = useContext(PopupContext);
  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.UPDATE_CATEGORY,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_CATEGORIES() });
      closePopup?.();
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.DELETE_CATEGORY,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_CATEGORIES() });
    },
  });
};

export const useGenerateCategories = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: PRODUCTS_MUTATIONS.GENERATE_CATEGORIES,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEYS.PRODUCT_CATEGORIES() });
    },
  });
};
